import { uniReactRoot } from '@affine/component';
import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import {
  createGroupByConfig,
  type GroupRenderProps,
  registerGlobalGroupByConfig,
  t,
  ungroups,
} from '@blocksuite/affine/blocks/database';
import type { UserService } from '@blocksuite/affine-shared/services';

import { useMemberInfo } from '../hooks/use-member-info';
import {
  avatar,
  memberName,
  memberPreviewContainer,
} from '../properties/member/style.css';
import { RelationGroupLabel } from './relation-group-label';

const MemberPreview = ({
  memberId,
  userService,
}: {
  memberId: string;
  userService: UserService | null | undefined;
}) => {
  const userInfo = useMemberInfo(memberId, userService);
  if (!userInfo) {
    return null;
  }
  return (
    <div className={memberPreviewContainer}>
      <CachedUserAvatar
        userId={memberId}
        name={userInfo.removed ? undefined : (userInfo.name ?? undefined)}
        className={avatar}
        url={!userInfo.removed ? userInfo.avatar : undefined}
        size={20}
      />
      <div className={memberName}>
        {userInfo.removed ? 'Deleted user' : userInfo.name || 'Unnamed'}
      </div>
    </div>
  );
};
const MemberGroupView = (props: GroupRenderProps<string | null, {}>) => {
  const tType = props.group.tType;
  if (!t.user.is(tType)) return 'Ungroup';
  const memberId = props.group.value;
  if (memberId == null) return 'Ungroup';

  return (
    <MemberPreview
      memberId={memberId}
      userService={tType.data?.userService}
    ></MemberPreview>
  );
};

const MultiMemberGroupView = (props: GroupRenderProps<string | null, {}>) => {
  const tType = props.group.tType;
  if (!t.array.is(tType) || !t.user.is(tType.element)) return 'Ungroup';
  const memberId = props.group.value;
  if (memberId == null) return 'Ungroup';

  return (
    <MemberPreview
      memberId={memberId}
      userService={tType.element.data?.userService}
    ></MemberPreview>
  );
};

const RelationGroupView = (
  props: GroupRenderProps<string | null, Record<string, unknown>>
) => {
  const docId = props.group.value;
  if (typeof docId !== 'string' || docId.length === 0) return 'Unassigned';
  const propData = props.group.property.data$.value as {
    targetDatabaseId?: string;
    targetFieldId?: string;
  };
  const targetDatabaseId = propData?.targetDatabaseId ?? '';
  const targetFieldId = propData?.targetFieldId ?? '';
  return (
    <RelationGroupLabel
      rowId={docId}
      targetDatabaseId={targetDatabaseId}
      targetFieldId={targetFieldId}
    />
  );
};

export const groupByConfigList = [
  createGroupByConfig({
    name: 'relation',
    matchType: t.array.instance(t.string.instance()),
    groupName: (_type, value: string | null) => {
      if (typeof value === 'string') return value;
      return '';
    },
    defaultKeys: () => [ungroups],
    valuesGroup: value => {
      if (!Array.isArray(value) || value.length === 0) return [ungroups];
      return value.map(id => ({ key: id, value: id }));
    },
    addToGroup: (value, old) => {
      if (value == null) return old;
      const existing = Array.isArray(old) ? old : [];
      return existing.includes(value) ? existing : [...existing, value];
    },
    removeFromGroup: (value, old) => {
      if (!Array.isArray(old)) return old;
      return old.filter(v => v !== value);
    },
    view: uniReactRoot.createUniComponent(RelationGroupView),
  }),
  createGroupByConfig({
    name: 'member',
    matchType: t.user.instance(),
    groupName: (type, value: string | null) => {
      if (t.user.is(type) && typeof value === 'string') {
        const userService = type.data?.userService;
        if (userService) {
          const userInfo = userService.userInfo$(value).value;
          if (userInfo && !userInfo?.removed) {
            return userInfo.name ?? 'Unnamed';
          }
        }
      }
      return '';
    },
    defaultKeys: () => {
      return [ungroups];
    },
    valuesGroup: value => {
      if (typeof value !== 'string') {
        return [ungroups];
      }
      return [
        {
          key: value,
          value: value,
        },
      ];
    },
    addToGroup: v => v,
    view: uniReactRoot.createUniComponent(MemberGroupView),
  }),
  createGroupByConfig({
    name: 'multi-member',
    matchType: t.array.instance(t.user.instance()),
    groupName: (_type, value: string | null) => {
      if (
        t.array.is(_type) &&
        t.user.is(_type.element) &&
        typeof value === 'string'
      ) {
        const userService = _type.element.data?.userService;
        if (userService) {
          const userInfo = userService.userInfo$(value).value;
          if (userInfo && !userInfo?.removed) {
            return userInfo.name ?? 'Unnamed';
          }
        }
      }
      return '';
    },
    defaultKeys: _type => {
      return [ungroups];
    },
    valuesGroup: (value, _type) => {
      if (!Array.isArray(value) || value.length === 0) {
        return [ungroups];
      }
      return value.map(id => ({
        key: id,
        value: id,
      }));
    },
    addToGroup: (value, old) => {
      if (value == null) {
        return old;
      }
      return Array.isArray(old) ? [...old, value] : [value];
    },
    removeFromGroup: (value, old) => {
      if (Array.isArray(old)) {
        return old.filter(v => v !== value);
      }
      return old;
    },
    view: uniReactRoot.createUniComponent(MultiMemberGroupView),
  }),
];

// Register at module-load time: bypass the DI-container split between
// editor-level std.provider and store-level store.provider.
registerGlobalGroupByConfig(...groupByConfigList);
